import React from "react";
import { Link } from "react-router-dom";
import { ReactComponent as BrandMark } from "../../../assets/svg/Dsahboard/brand-stepup-icon.svg";
import { ReactComponent as PlansMark } from "../../../assets/svg/Dsahboard/plans-icon.svg";
import { ReactComponent as AddClientMark } from "../../../assets/svg/Dsahboard/add-client-icon.svg";
import brandRipple from "../../../assets/svg/Dsahboard/brand-ripple.json";
import plansRipple from "../../../assets/svg/Dsahboard/plans-ripple.json";
import clientRipple from "../../../assets/svg/Dsahboard/client-ripple.json";
import StepRipple from "./StepRipple";
import type { SetupStep, SetupStepId } from "../setupSteps";

/**
 * The disc at the head of a step, the halo behind it, and the colour its note
 * is written in.
 *
 * Each step keeps its own mark and its own colour whatever state it is in — a
 * completed step reads "Completed" in the colour it has had all along rather
 * than swapping to a tick, because the row is a place in a journey and not a
 * checkbox. The word is what says which state it is in, so the colour is never
 * the only thing carrying it.
 *
 * Nothing is greyed back, including a step waiting its turn. It has no link and
 * no "+ …" offer — see `PENDING_NOTE` in `setupSteps` — and that is enough to
 * say it is not ready without dimming a row the reader is meant to be looking
 * forward to.
 *
 * The discs are part of the artwork rather than a tint under a glyph: each file
 * carries its own circle and its own stroke, at 48px. The `ripple` beside it is
 * the loop that plays under the one step the reader is on — see `liveStep`.
 */
const STEP_STYLES: Record<
  SetupStepId,
  { Mark: React.ElementType; ripple: unknown; note: string }
> = {
  brand: { Mark: BrandMark, ripple: brandRipple, note: "#43AF87" },
  plans: { Mark: PlansMark, ripple: plansRipple, note: "#7337B0" },
  client: { Mark: AddClientMark, ripple: clientRipple, note: "#00BACB" },
};

/** The leg between two steps, travelled and not. */
const LEG_DONE = "#00899B";
const LEG_TODO = "#E9EDF1";

/**
 * Which step gets the halo: the one that is theirs to do, and only that one.
 *
 * That is the step with somewhere to go — a completed one is behind them and
 * one waiting its turn cannot be pressed, so neither has anything to draw the
 * eye to, and three loops pulsing at once would point at nothing. A list with
 * nothing left on it gets no halo at all.
 */
const liveStep = (steps: SetupStep[]) =>
  steps.findIndex((step) => step.state === "todo" && step.to);

interface SetupChecklistProps {
  steps: SetupStep[];
}

/**
 * The padding a step sits in, so the one that is a link has somewhere to
 * paint its hover without standing lower than the two beside it. Every row
 * carries it, and the list pulls the outermost 8px back out.
 *
 * Full width stacked, and only as wide as its words on one line — what is left
 * over there belongs to the legs, which is what makes them stretch.
 */
const ROW =
  "w-full sm:w-auto sm:min-w-0 p-2 rounded-md flex items-center gap-3";

/**
 * What the welcome card shows a workspace that is still being set up: the three
 * things standing between it and a dashboard with figures on it.
 *
 * A step with somewhere to go is a link, and one without is not — pressing a
 * completed step would send the reader back to work they have finished, and one
 * still waiting its turn has nowhere to send them yet. Both are drawn the same
 * as the live step; only the note and the hover tell them apart.
 *
 * From `sm` the three sit on one line joined by legs; below it they stack and
 * the legs go, because a horizontal rule between stacked rows joins nothing.
 */
const SetupChecklist: React.FC<SetupChecklistProps> = ({ steps }) => {
  const live = liveStep(steps);

  return (
    <ul className="mt-3 sm:mt-4 -mx-2 flex flex-col sm:flex-row sm:items-center">
      {steps.map((step, index) => {
        const style = STEP_STYLES[step.id];
        const Mark = style.Mark;
        const to = step.to;

        const body = (
          <>
            {/* The mark, with the halo behind it on the live step. Both sit in
                one box so the ripple can be centred on the disc and run wider
                than it without pushing the words along. */}
            <span className="relative flex-shrink-0 w-12 h-12">
              {index === live && <StepRipple data={style.ripple} />}
              <Mark aria-hidden className="relative w-12 h-12" />
            </span>
            <span className="min-w-0 text-left">
              <span className="block text-[14px] font-medium truncate text-ink">
                {step.title}
              </span>
              <span
                className="block text-[12px] font-medium truncate"
                style={{ color: style.note }}
              >
                {step.note}
              </span>
            </span>
          </>
        );

        return (
          // The row carrying a leg takes an equal share of what is left over,
          // so the two legs grow and shrink together as the card does.
          <li
            key={step.id}
            className={
              index === 0
                ? "sm:flex-shrink-0"
                : "flex items-center sm:flex-1 sm:min-w-0"
            }
          >
            {index > 0 && (
              // The leg into this step, drawn as travelled once the step before
              // it is done — and the first one always, since the reader is
              // standing at step one the moment the card opens.
              <span
                aria-hidden
                className="hidden sm:block h-px flex-1 min-w-[24px] mx-3"
                style={{
                  backgroundColor:
                    index === 1 || steps[index - 1].state === "done"
                      ? LEG_DONE
                      : LEG_TODO,
                }}
              />
            )}
            {to ? (
              // The whole row is the target, so what is pressed is the step
              // rather than the few words inside it — the "+ Add now" under
              // the title is where the eye lands, not a second control.
              //
              // A link rather than a button that navigates: it goes to a
              // screen of the app, so it behaves like every other way of
              // getting there — a middle click opens it in a tab, the status
              // bar says where it leads, and the row still reads as one step.
              <Link
                to={to}
                className={`${ROW} transition-colors hover:bg-[#F7FBFC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40`}
              >
                {body}
              </Link>
            ) : (
              <div className={ROW}>{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default SetupChecklist;
